import React from "react";
import { Button } from "@/components/ui/button";
import { Package, CalendarCheck, Images, Calendar, MapPin, CalendarDays, DollarSign, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { ChatMessage } from "@/hooks/useChat";

interface QuickReply {
  label: string;
  message: string;
  icon: React.ReactNode;
}

interface ConversationData {
  user_name?: string;
  wedding_date?: string;
  venue?: string;
  services?: string[];
}

const quickReplySets = {
  initial: [
    { label: "View packages", message: "What packages do you offer?", icon: <Package className="h-3 w-3" /> },
    { label: "Check availability", message: "Are you available for my wedding date?", icon: <CalendarCheck className="h-3 w-3" /> },
    { label: "See portfolio", message: "Can I see some of your work?", icon: <Images className="h-3 w-3" /> },
  ],
  collectDate: [
    { label: "Share my date", message: "I'd like to share my wedding date with you", icon: <CalendarDays className="h-3 w-3" /> },
    { label: "View packages", message: "What packages do you offer?", icon: <Package className="h-3 w-3" /> },
    { label: "See portfolio", message: "Can I see some of your work?", icon: <Images className="h-3 w-3" /> },
  ],
  collectVenue: [
    { label: "Share my venue", message: "Let me tell you about our venue", icon: <MapPin className="h-3 w-3" /> },
    { label: "View packages", message: "What packages do you offer?", icon: <Package className="h-3 w-3" /> },
    { label: "Check availability", message: "Are you available for my date?", icon: <CalendarCheck className="h-3 w-3" /> },
  ],
  afterPricing: [
    { label: "Book a call", message: "I'd like to schedule a consultation!", icon: <Calendar className="h-3 w-3" /> },
    { label: "Compare packages", message: "Can you help me compare the packages?", icon: <Package className="h-3 w-3" /> },
    { label: "See portfolio", message: "Can I see some of your work?", icon: <Images className="h-3 w-3" /> },
  ],
  readyToBook: [
    { label: "Schedule consultation", message: "I'd love to schedule a call to discuss our wedding!", icon: <Calendar className="h-3 w-3" /> },
    { label: "Get custom quote", message: "Can I get a custom quote for my wedding?", icon: <DollarSign className="h-3 w-3" /> },
    { label: "Have more questions", message: "I have a few more questions first", icon: <HelpCircle className="h-3 w-3" /> },
  ],
  afterPortfolio: [
    { label: "Book a call", message: "I'd like to schedule a consultation!", icon: <Calendar className="h-3 w-3" /> },
    { label: "View packages", message: "What packages do you offer?", icon: <Package className="h-3 w-3" /> },
    { label: "Check availability", message: "Are you available for my date?", icon: <CalendarCheck className="h-3 w-3" /> },
  ],
};

type ConversationStage = keyof typeof quickReplySets;

function getConversationStage(conversationData: ConversationData, messages: ChatMessage[]): ConversationStage {
  const allMessagesText = messages.map(m => m.content.toLowerCase()).join(" ");
  
  const pricingDiscussed = allMessagesText.includes("package") || 
                           allMessagesText.includes("price") || 
                           allMessagesText.includes("cost") ||
                           allMessagesText.includes("$") ||
                           allMessagesText.includes("investment");
  const portfolioDiscussed = allMessagesText.includes("portfolio") || 
                              allMessagesText.includes("gallery") ||
                              allMessagesText.includes("work") ||
                              allMessagesText.includes("photos");
  
  const hasName = !!conversationData.user_name;
  const hasDate = !!conversationData.wedding_date;
  const hasVenue = !!conversationData.venue;
  const hasServices = (conversationData.services?.length ?? 0) > 0;
  
  if (hasName && hasDate && (hasServices || pricingDiscussed)) {
    return "readyToBook";
  }
  
  if (pricingDiscussed) {
    return "afterPricing";
  }
  
  if (portfolioDiscussed && !pricingDiscussed) {
    return "afterPortfolio";
  }
  
  if (hasName && !hasDate) {
    return "collectDate";
  }
  
  if (hasDate && !hasVenue) {
    return "collectVenue";
  }
  
  return "initial";
}

interface QuickRepliesProps {
  onSelect: (message: string) => void;
  disabled?: boolean;
  conversationData?: ConversationData;
  messages?: ChatMessage[];
  conversationId?: string;
}

async function trackQuickReplyClick(
  label: string,
  message: string,
  stage: string,
  conversationId?: string
) {
  try {
    await supabase.from("quick_reply_analytics").insert({
      conversation_id: conversationId || null,
      button_label: label,
      button_message: message,
      conversation_stage: stage,
    });
  } catch (error) {
    console.error("Failed to track quick reply click:", error);
  }
}

export const QuickReplies = React.forwardRef<HTMLDivElement, QuickRepliesProps>(
  ({ onSelect, disabled, conversationData = {}, messages = [], conversationId }, ref) => {
    const stage = getConversationStage(conversationData, messages);
    const quickReplies = quickReplySets[stage];
    
    const handleClick = (reply: QuickReply) => {
      trackQuickReplyClick(reply.label, reply.message, stage, conversationId);
      onSelect(reply.message);
    };
    
    return (
      <div ref={ref} className="flex flex-wrap gap-2 pl-11">
        {quickReplies.map((reply) => (
          <Button
            key={reply.label}
            variant="outline"
            size="sm"
            onClick={() => handleClick(reply)}
            disabled={disabled}
            className="h-7 gap-1.5 rounded-full border-primary/20 bg-background px-3 text-xs text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
          >
            {reply.icon}
            {reply.label}
          </Button>
        ))}
      </div>
    );
  }
);

QuickReplies.displayName = "QuickReplies";
